import React, { useMemo, useState } from 'react';
import Layout from '../../../components/Layout';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ============================================================================
 * MOCKUP — Sezione "Righe preventivo" — Proposta C "Misto"
 *
 * Decisioni Paolo:
 *  - 3 livelli con etichette LIBERE
 *  - Drill-down stile C: accordion + bottone "focalizza" per zoomare su sotto-albero
 *  - DnD totale con vincoli MISTI:
 *      L1 ↔ L1 (riordino, no cambio livello)
 *      L2 può cambiare parent L1 e riordinare tra fratelli L2 (no cambio livello)
 *      L3 può cambiare parent L2 e riordinare tra fratelli L3 (no cambio livello)
 *      Righe possono essere droppate in qualunque gruppo (L1, L2, L3)
 *  - Creazione nuovi gruppi contestuale ai 3 livelli (input + Aggiungi)
 *
 * Libreria: @dnd-kit/core + @dnd-kit/sortable + @dnd-kit/utilities
 * Nessuna chiamata API. Stato locale.
 *
 * NOTA: per eseguire questo mockup serve:
 *   npm install @dnd-kit/core @dnd-kit/sortable @dnd-kit/utilities
 * ============================================================================ */

// ── Icone ───────────────────────────────────────────────────────────────────

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const FocusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V3h4" /><path d="M21 7V3h-4" /><path d="M3 17v4h4" /><path d="M21 17v4h-4" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const GripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

// ── Tipi ────────────────────────────────────────────────────────────────────

type Livello = 1 | 2 | 3;

interface Riga {
  id: number;
  ordine: number;
  descrizione: string;
  codice?: string;
  um?: string;
  qta?: number;
  prezzo?: number;
  sconto?: number;
  importo?: number;
  alternativaGruppo?: string;
  alternativaScelta?: boolean;
  etichettaAlternativa?: string;
  tipo: 'ARTICOLO' | 'ARTICOLO_CONFIGURATO' | 'DESCRITTIVA';
  // Riferimento al gruppo di appartenenza (parent diretto, può essere L1, L2 o L3)
  gruppoId: number;
}

interface Gruppo {
  id: number;
  livello: Livello;
  parentId: number | null;
  ordine: number;
  etichetta: string;
}

// ── Dataset iniziale ────────────────────────────────────────────────────────

const GRUPPI_INIT: Gruppo[] = [
  { id: 1, livello: 1, parentId: null, ordine: 1, etichetta: 'Zona giorno' },
  { id: 11, livello: 2, parentId: 1, ordine: 1, etichetta: 'Soggiorno' },
  { id: 111, livello: 3, parentId: 11, ordine: 1, etichetta: 'Battiscopa e accessori' },
  { id: 12, livello: 2, parentId: 1, ordine: 2, etichetta: 'Cucina' },

  { id: 2, livello: 1, parentId: null, ordine: 2, etichetta: 'Zona notte' },
  { id: 21, livello: 2, parentId: 2, ordine: 1, etichetta: 'Camera matrimoniale' },
  { id: 22, livello: 2, parentId: 2, ordine: 2, etichetta: 'Bagno padronale' },
  { id: 221, livello: 3, parentId: 22, ordine: 1, etichetta: 'Pavimento' },
  { id: 222, livello: 3, parentId: 22, ordine: 2, etichetta: 'Rivestimento doccia' },
];

const RIGHE_INIT: Riga[] = [
  // Soggiorno (L2 — id 11)
  { id: 1001, gruppoId: 11, ordine: 1, tipo: 'ARTICOLO_CONFIGURATO',
    codice: 'PC-PAVI-A', descrizione: 'Pavimento gres porcellanato 60x60 — Tortora',
    um: 'mq', qta: 28, prezzo: 38.50, sconto: 0, importo: 1078.00 },

  // Battiscopa (L3 — id 111)
  { id: 1002, gruppoId: 111, ordine: 1, tipo: 'ARTICOLO',
    codice: 'BATT-001', descrizione: 'Battiscopa MDF laccato bianco h.7',
    um: 'ml', qta: 22, prezzo: 4.20, sconto: 0, importo: 92.40 },
  { id: 1003, gruppoId: 111, ordine: 2, tipo: 'ARTICOLO',
    codice: 'PROF-INOX', descrizione: 'Profilo terminale acciaio inox',
    um: 'pz', qta: 4, prezzo: 12.00, sconto: 5, importo: 45.60 },

  // Cucina (L2 — id 12) — con cluster alternative
  { id: 1010, gruppoId: 12, ordine: 1, tipo: 'DESCRITTIVA',
    descrizione: 'Fornitura e posa rivestimento parete cottura, h. 60 cm dal piano lavoro.' },
  { id: 1011, gruppoId: 12, ordine: 2, tipo: 'ARTICOLO',
    codice: 'RIV-CUC-X1', descrizione: 'Rivestimento ceramico 25x40 — Bianco lucido',
    um: 'mq', qta: 6, prezzo: 22.00, sconto: 0, importo: 132.00,
    alternativaGruppo: 'A', alternativaScelta: true, etichettaAlternativa: 'Standard' },
  { id: 1012, gruppoId: 12, ordine: 3, tipo: 'ARTICOLO',
    codice: 'RIV-CUC-X2', descrizione: 'Rivestimento ceramico 25x40 — Decoro Floreale',
    um: 'mq', qta: 6, prezzo: 34.00, sconto: 0, importo: 204.00,
    alternativaGruppo: 'A', alternativaScelta: false, etichettaAlternativa: 'Premium' },

  // Camera matrimoniale (L2 — id 21)
  { id: 2001, gruppoId: 21, ordine: 1, tipo: 'ARTICOLO_CONFIGURATO',
    codice: 'PC-PARQ-RV', descrizione: 'Parquet prefinito rovere 14mm',
    um: 'mq', qta: 18, prezzo: 62.00, sconto: 5, importo: 1060.20 },

  // Bagno → Pavimento (L3 — id 221)
  { id: 2210, gruppoId: 221, ordine: 1, tipo: 'ARTICOLO',
    codice: 'PAV-MARM', descrizione: 'Gres effetto marmo 60x120',
    um: 'mq', qta: 8, prezzo: 48.00, sconto: 0, importo: 384.00 },

  // Bagno → Rivestimento doccia (L3 — id 222)
  { id: 2220, gruppoId: 222, ordine: 1, tipo: 'ARTICOLO',
    codice: 'RIV-DOC', descrizione: 'Mosaico 30x30 — Beige lucido',
    um: 'mq', qta: 4.5, prezzo: 95.00, sconto: 10, importo: 384.75 },
];

// ── Helpers struttura ───────────────────────────────────────────────────────

function fmt(n: number | undefined): string {
  if (n == null) return '';
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── ID convenzionali per @dnd-kit ───────────────────────────────────────────
// Usiamo prefissi per distinguere il tipo di entità draggabile:
//   "G:<id>"  = gruppo
//   "R:<id>"  = riga
// I droppable di gruppo hanno id = "G:<id>" (i gruppi sono sia draggabili che droppable).
// Per droppare righe come "primo elemento" di un gruppo vuoto, usiamo "GE:<id>" (gruppo-empty-zone).

const idGruppo = (n: number) => `G:${n}`;
const idRiga = (n: number) => `R:${n}`;
const idEmptyZone = (n: number) => `GE:${n}`;

function parseDndId(s: string | number): { kind: 'G' | 'R' | 'GE'; id: number } | null {
  const str = String(s);
  const m = str.match(/^(G|R|GE):(\d+)$/);
  if (!m) return null;
  return { kind: m[1] as 'G' | 'R' | 'GE', id: parseInt(m[2], 10) };
}

// ── Riga del datatable (trascinabile) ───────────────────────────────────────

const SortableRiga: React.FC<{ r: Riga; numero: number }> = ({ r, numero }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: idRiga(r.id),
    data: { type: 'riga', gruppoId: r.gruppoId },
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const tipoCod = r.tipo === 'ARTICOLO' ? 'ART' : r.tipo === 'ARTICOLO_CONFIGURATO' ? 'CFG' : 'DSC';

  return (
    <tr ref={setNodeRef} style={style} className="riga-row">
      <td className="col-grip" {...attributes} {...listeners} title="Trascina per spostare">
        <GripIcon />
      </td>
      <td className="col-num">{numero}</td>
      <td className="col-tipo"><span className={`tipo-badge tipo-badge--${tipoCod}`}>{tipoCod}</span></td>
      <td className="col-codice">{r.codice || '—'}</td>
      <td>
        {r.descrizione}
        {r.alternativaScelta === false && <span className="bop-text-muted" style={{ marginLeft: 6 }}>(non scelta)</span>}
        {r.alternativaScelta === true && r.alternativaGruppo && <span className="scelta-badge">SCELTA</span>}
        {r.etichettaAlternativa && <span className="bop-text-muted" style={{ marginLeft: 6, fontSize: 11 }}>· {r.etichettaAlternativa}</span>}
      </td>
      <td>{r.um || '—'}</td>
      <td className="num">{fmt(r.qta)}</td>
      <td className="num">{fmt(r.prezzo)}</td>
      <td className="num">{r.sconto ? fmt(r.sconto) : '—'}</td>
      <td className="num"><strong>{fmt(r.importo)}</strong></td>
      <td className="col-azioni">
        <button className="bop-btn-icon" title="Modifica"><EditIcon /></button>
        <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
      </td>
    </tr>
  );
};

// ── Tabella righe (sortable list di righe) ──────────────────────────────────

const RigheTable: React.FC<{ righe: Riga[] }> = ({ righe }) => {
  if (!righe.length) return null;

  // Riordino visuale per ordine
  const sorted = [...righe].sort((a, b) => a.ordine - b.ordine);

  // Identifico i cluster alternative (header decorativi tra le righe)
  const blocks: Array<
    | { kind: 'normal'; r: Riga }
    | { kind: 'cluster-header'; gruppo: string; numAlt: number; scelta?: string }
  > = [];
  let lastCluster: string | null = null;
  for (const r of sorted) {
    if (r.alternativaGruppo && r.alternativaGruppo !== lastCluster) {
      const numAlt = sorted.filter(x => x.alternativaGruppo === r.alternativaGruppo).length;
      const scelta = sorted.find(x => x.alternativaGruppo === r.alternativaGruppo && x.alternativaScelta);
      blocks.push({ kind: 'cluster-header', gruppo: r.alternativaGruppo, numAlt, scelta: scelta?.descrizione });
      lastCluster = r.alternativaGruppo;
    } else if (!r.alternativaGruppo) {
      lastCluster = null;
    }
    blocks.push({ kind: 'normal', r });
  }

  let visibleNum = 0;

  return (
    <SortableContext items={sorted.map(r => idRiga(r.id))} strategy={verticalListSortingStrategy}>
      <table className="righe-table">
        <thead>
          <tr>
            <th className="col-grip"></th>
            <th className="col-num">#</th>
            <th className="col-tipo">Tipo</th>
            <th className="col-codice">Codice</th>
            <th>Descrizione</th>
            <th className="col-um">U.M.</th>
            <th className="num">Qta</th>
            <th className="num">Prezzo</th>
            <th className="num">Sconto%</th>
            <th className="num">Importo</th>
            <th className="col-azioni"></th>
          </tr>
        </thead>
        <tbody>
          {blocks.map((b, i) => {
            if (b.kind === 'cluster-header') {
              return (
                <tr key={`c-${i}`} className="cluster-row">
                  <td colSpan={11}>
                    <span className="alt-badge">ALT {b.gruppo}</span>
                    {b.numAlt} alternativ{b.numAlt === 1 ? 'a' : 'e'}
                    {b.scelta && <> · scelta: <strong>{b.scelta}</strong></>}
                  </td>
                </tr>
              );
            }
            visibleNum++;
            return <SortableRiga key={b.r.id} r={b.r} numero={visibleNum} />;
          })}
        </tbody>
      </table>
    </SortableContext>
  );
};

// ── DragOverlay (preview durante il drag) ───────────────────────────────────

const RigaDragPreview: React.FC<{ r: Riga }> = ({ r }) => (
  <div className="drag-preview">
    <GripIcon />
    <span className="drag-preview-codice">{r.codice || '—'}</span>
    <span className="drag-preview-desc">{r.descrizione}</span>
    {r.importo != null && <span className="drag-preview-imp">€ {fmt(r.importo)}</span>}
  </div>
);

const GruppoDragPreview: React.FC<{ g: Gruppo; nRighe: number; importo: number }> = ({ g, nRighe, importo }) => (
  <div className={`drag-preview drag-preview--gruppo drag-preview--l${g.livello}`}>
    <GripIcon />
    <span className="drag-preview-livello">L{g.livello}</span>
    <span className="drag-preview-desc"><strong>{g.etichetta}</strong></span>
    <span className="drag-preview-imp">{nRighe} righe · € {fmt(importo)}</span>
  </div>
);

// ── Box "+ Aggiungi gruppo" contestuale ─────────────────────────────────────

const AggiungiGruppoBox: React.FC<{
  livello: Livello;
  parentId: number | null;
  parentLabel?: string;
  onAdd: (etichetta: string) => void;
}> = ({ livello, parentId, parentLabel, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const placeholder =
    livello === 1 ? 'Etichetta sezione di livello 1' :
    livello === 2 ? `Etichetta sezione di livello 2${parentLabel ? ` (in ${parentLabel})` : ''}` :
                    `Etichetta sezione di livello 3${parentLabel ? ` (in ${parentLabel})` : ''}`;

  const tipoNome =
    livello === 1 ? 'sezione di livello 1' :
    livello === 2 ? 'sezione di livello 2' :
                    'sezione di livello 3';

  const conferma = () => {
    const v = value.trim();
    if (!v) return;
    onAdd(v);
    setValue('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button className="add-gruppo-trigger" onClick={() => setOpen(true)}>
        <PlusIcon /> Aggiungi {tipoNome}
        {parentLabel && <span className="add-gruppo-trigger-ctx"> in "{parentLabel}"</span>}
      </button>
    );
  }

  return (
    <div className="add-gruppo-box">
      <input
        className="bop-input"
        autoFocus
        placeholder={placeholder}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') conferma();
          if (e.key === 'Escape') { setOpen(false); setValue(''); }
        }}
      />
      <button className="bop-btn bop-btn-primary" onClick={conferma} disabled={!value.trim()}>Aggiungi</button>
      <button className="bop-btn bop-btn-secondary" onClick={() => { setOpen(false); setValue(''); }}>Annulla</button>
    </div>
  );
};

// ── Componenti gruppo (draggabili e droppabili tramite useSortable) ─────────

interface RenderCtx {
  gruppi: Gruppo[];
  righe: Riga[];
  openL1: Set<number>; toggleL1: (id: number) => void;
  openL2: Set<number>; toggleL2: (id: number) => void;
  openL3: Set<number>; toggleL3: (id: number) => void;
  focusId: number | null;
  setFocusId: (id: number | null) => void;
  isInFocus: (g: Gruppo) => boolean;
  righeIn: (gruppoId: number) => Riga[];
  totaleRigheRicorsivo: (gruppoId: number) => number;
  importoRicorsivo: (gruppoId: number) => number;
  aggiungiGruppo: (livello: Livello, parentId: number | null, etichetta: string) => void;
  draggingGruppoId: number | null;
}

const SortableGruppo: React.FC<{ g: Gruppo; ctx: RenderCtx }> = ({ g, ctx }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: idGruppo(g.id),
    data: { type: 'gruppo', livello: g.livello, parentId: g.parentId },
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const isOpen =
    g.livello === 1 ? ctx.openL1.has(g.id) :
    g.livello === 2 ? ctx.openL2.has(g.id) :
    ctx.openL3.has(g.id);

  const toggle = () =>
    g.livello === 1 ? ctx.toggleL1(g.id) :
    g.livello === 2 ? ctx.toggleL2(g.id) :
    ctx.toggleL3(g.id);

  const figli = ctx.gruppi
    .filter(x => x.parentId === g.id)
    .sort((a, b) => a.ordine - b.ordine);

  const righeDirette = ctx.righeIn(g.id);

  return (
    <div ref={setNodeRef} style={style} className={`gp gp-l${g.livello}`}>
      <div className="gp-header" onClick={toggle}>
        <span
          className="gp-grip"
          onClick={e => e.stopPropagation()}
          {...attributes}
          {...listeners}
          title="Trascina per riordinare"
        >
          <GripIcon />
        </span>
        <span className="gp-chevron">{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</span>
        <span className="gp-livello-tag">L{g.livello}</span>
        <span className="gp-label">{g.etichetta}</span>
        <span className="gp-meta">
          {ctx.totaleRigheRicorsivo(g.id)} righe · € {fmt(ctx.importoRicorsivo(g.id))}
        </span>
        <span className="gp-actions" onClick={e => e.stopPropagation()}>
          <button className="bop-btn-icon" title="Focalizza su questo gruppo" onClick={() => ctx.setFocusId(g.id)}>
            <FocusIcon />
          </button>
          <button className="bop-btn-icon" title="Rinomina"><EditIcon /></button>
          <button className="bop-btn-icon" title="Elimina"><TrashIcon /></button>
        </span>
      </div>

      {isOpen && (
        <GruppoBody
          g={g}
          ctx={ctx}
          righeDirette={righeDirette}
          figli={figli}
        />
      )}
    </div>
  );
};

// Corpo del gruppo: righe dirette + figli + bottoni "aggiungi"
const GruppoBody: React.FC<{
  g: Gruppo;
  ctx: RenderCtx;
  righeDirette: Riga[];
  figli: Gruppo[];
}> = ({ g, ctx, righeDirette, figli }) => {
  // Empty zone droppable (per ricevere righe quando il gruppo non ha righe dirette)
  const { setNodeRef: emptyRef, isOver: emptyOver } = useSortable({
    id: idEmptyZone(g.id),
    data: { type: 'empty-zone', gruppoId: g.id },
    disabled: righeDirette.length > 0, // attivo solo se non ci sono righe
  });

  const figliL = (g.livello + 1) as Livello;
  const canHaveChildren = g.livello < 3;

  return (
    <div className="gp-body">
      {/* Righe dirette (se presenti, sono sortabili) */}
      {righeDirette.length > 0 && <RigheTable righe={righeDirette} />}

      {/* Empty zone droppable per ricevere la prima riga */}
      {righeDirette.length === 0 && (
        <div ref={emptyRef} className={`empty-rows-zone ${emptyOver ? 'empty-rows-zone--over' : ''}`}>
          Nessuna riga in <em>{g.etichetta}</em> · trascina qui una riga oppure
          <button className="link-add"><PlusIcon /> aggiungi riga</button>
        </div>
      )}

      {/* Sotto-gruppi figli (ricorsivo, in SortableContext separato per livello) */}
      {canHaveChildren && figli.length > 0 && (
        <SortableContext
          items={figli.map(f => idGruppo(f.id))}
          strategy={verticalListSortingStrategy}
        >
          <div className="gp-children">
            {figli.map(f => <SortableGruppo key={f.id} g={f} ctx={ctx} />)}
          </div>
        </SortableContext>
      )}

      {/* Aggiungi sotto-gruppo */}
      {canHaveChildren && (
        <div className="gp-add-child">
          <AggiungiGruppoBox
            livello={figliL}
            parentId={g.id}
            parentLabel={g.etichetta}
            onAdd={(et) => ctx.aggiungiGruppo(figliL, g.id, et)}
          />
        </div>
      )}

      {/* Bottone aggiungi riga sempre */}
      <button className="link-add link-add--inline">
        <PlusIcon /> Aggiungi riga in "{g.etichetta}"
      </button>
    </div>
  );
};

// ── Pagina principale ───────────────────────────────────────────────────────

const MockupRighePreventivoDrilldownC: React.FC = () => {
  const [gruppi, setGruppi] = useState<Gruppo[]>(GRUPPI_INIT);
  const [righe, setRighe] = useState<Riga[]>(RIGHE_INIT);
  const [openL1, setOpenL1] = useState<Set<number>>(new Set([1, 2]));
  const [openL2, setOpenL2] = useState<Set<number>>(new Set([11, 12, 21, 22]));
  const [openL3, setOpenL3] = useState<Set<number>>(new Set([111, 221, 222]));
  const [focusId, setFocusId] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeKind, setActiveKind] = useState<'gruppo' | 'riga' | null>(null);

  // ── Sensori @dnd-kit ─────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggle = (s: Set<number>, setS: (n: Set<number>) => void, id: number) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    setS(next);
  };

  const figliDi = (parentId: number | null, livello: Livello): Gruppo[] =>
    gruppi.filter(g => g.parentId === parentId && g.livello === livello).sort((a, b) => a.ordine - b.ordine);

  const righeIn = (gruppoId: number): Riga[] =>
    righe.filter(r => r.gruppoId === gruppoId).sort((a, b) => a.ordine - b.ordine);

  const totaleRigheRicorsivo = (gruppoId: number): number => {
    let n = righe.filter(r => r.gruppoId === gruppoId).length;
    for (const figlio of gruppi.filter(g => g.parentId === gruppoId)) {
      n += totaleRigheRicorsivo(figlio.id);
    }
    return n;
  };

  const importoRicorsivo = (gruppoId: number): number => {
    let tot = righe
      .filter(r => r.gruppoId === gruppoId && (!r.alternativaGruppo || r.alternativaScelta))
      .reduce((s, r) => s + (r.importo || 0), 0);
    for (const figlio of gruppi.filter(g => g.parentId === gruppoId)) {
      tot += importoRicorsivo(figlio.id);
    }
    return tot;
  };

  const isInFocus = (g: Gruppo): boolean => {
    if (!focusId) return true;
    if (g.id === focusId) return true;
    let cur: number | null = g.parentId;
    while (cur !== null) {
      if (cur === focusId) return true;
      const p = gruppi.find(x => x.id === cur);
      cur = p?.parentId ?? null;
    }
    // E se il focus è SU un figlio di g, mostro anche g? No: focus = isolare il sotto-albero del nodo focalizzato
    return false;
  };

  const focusPath = useMemo(() => {
    if (!focusId) return [] as Gruppo[];
    const out: Gruppo[] = [];
    let cur = gruppi.find(g => g.id === focusId);
    while (cur) {
      out.unshift(cur);
      cur = cur.parentId ? gruppi.find(x => x.id === cur!.parentId) : undefined;
    }
    return out;
  }, [focusId, gruppi]);

  // ── Aggiungi gruppo ───────────────────────────────────────────────────────
  const aggiungiGruppo = (livello: Livello, parentId: number | null, etichetta: string) => {
    const nuovoOrdine = (gruppi.filter(g => g.parentId === parentId && g.livello === livello)
      .reduce((m, g) => Math.max(m, g.ordine), 0)) + 1;
    const id = Math.max(0, ...gruppi.map(g => g.id)) + 1;
    setGruppi([...gruppi, { id, livello, parentId, ordine: nuovoOrdine, etichetta }]);
    // Apri automaticamente il nuovo gruppo
    if (livello === 1) setOpenL1(new Set([...Array.from(openL1), id]));
    if (livello === 2) setOpenL2(new Set([...Array.from(openL2), id]));
    if (livello === 3) setOpenL3(new Set([...Array.from(openL3), id]));
  };

  // ── Drag handlers ─────────────────────────────────────────────────────────

  // Strategia di rilevamento collisioni: prima pointerWithin (preciso),
  // poi rectIntersection (fallback per gruppi grandi), poi closestCenter.
  const collisionDetection: CollisionDetection = (args) => {
    const pw = pointerWithin(args);
    if (pw.length > 0) return pw;
    const ri = rectIntersection(args);
    if (ri.length > 0) return ri;
    return closestCenter(args);
  };

  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    setActiveId(id);
    const data = e.active.data.current;
    setActiveKind(data?.type === 'gruppo' ? 'gruppo' : 'riga');
  };

  // Helper: scopri il gruppo di destinazione a partire da un over.id qualsiasi
  const findTargetGruppoId = (overId: string): number | null => {
    const parsed = parseDndId(overId);
    if (!parsed) return null;
    if (parsed.kind === 'G') return parsed.id;          // dropped sopra un gruppo
    if (parsed.kind === 'GE') return parsed.id;         // dropped in empty-zone
    if (parsed.kind === 'R') {                          // dropped sopra una riga → eredita gruppoId
      const r = righe.find(x => x.id === parsed.id);
      return r?.gruppoId ?? null;
    }
    return null;
  };

  // Spostamento riga tra gruppi durante il drag (per feedback live)
  const onDragOver = (e: DragOverEvent) => {
    const { active, over } = e;
    if (!over) return;

    const activeParsed = parseDndId(String(active.id));
    if (!activeParsed || activeParsed.kind !== 'R') return;

    const overParsed = parseDndId(String(over.id));
    if (!overParsed) return;

    const rigaTrascinata = righe.find(r => r.id === activeParsed.id);
    if (!rigaTrascinata) return;

    // Se la riga sta finendo in un gruppo diverso, cambia il gruppoId temporaneamente
    const targetGruppoId = findTargetGruppoId(String(over.id));
    if (!targetGruppoId || targetGruppoId === rigaTrascinata.gruppoId) return;

    // Sposta la riga nel nuovo gruppo (ordine = fine)
    setRighe(curr => {
      const fonte = curr.find(r => r.id === activeParsed.id);
      if (!fonte) return curr;
      if (fonte.gruppoId === targetGruppoId) return curr;
      const inDest = curr.filter(r => r.gruppoId === targetGruppoId);
      const nuovoOrdine = (inDest.reduce((m, r) => Math.max(m, r.ordine), 0)) + 1;
      return curr.map(r =>
        r.id === activeParsed.id ? { ...r, gruppoId: targetGruppoId, ordine: nuovoOrdine } : r
      );
    });
  };

  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    setActiveKind(null);

    const { active, over } = e;
    if (!over) return;
    if (active.id === over.id) return;

    const aP = parseDndId(String(active.id));
    const oP = parseDndId(String(over.id));
    if (!aP || !oP) return;

    // ── Caso 1: drag riga ──────────────────────────────────────────────────
    if (aP.kind === 'R') {
      const aRiga = righe.find(r => r.id === aP.id);
      if (!aRiga) return;

      // Se ho droppato sopra un'altra riga: riordino dentro il gruppo target
      if (oP.kind === 'R') {
        const oRiga = righe.find(r => r.id === oP.id);
        if (!oRiga) return;
        // Le righe ora sono nello stesso gruppo (onDragOver ha già spostato la riga)
        const inGruppo = righe
          .filter(r => r.gruppoId === oRiga.gruppoId)
          .sort((x, y) => x.ordine - y.ordine);
        const oldIdx = inGruppo.findIndex(r => r.id === aP.id);
        const newIdx = inGruppo.findIndex(r => r.id === oP.id);
        if (oldIdx === -1 || newIdx === -1) return;
        const riordinato = arrayMove(inGruppo, oldIdx, newIdx);
        setRighe(curr =>
          curr.map(r => {
            const idxNuovo = riordinato.findIndex(x => x.id === r.id);
            if (idxNuovo === -1) return r;
            return { ...r, ordine: idxNuovo + 1 };
          })
        );
      }
      // Se ho droppato su un gruppo o su una empty-zone, onDragOver ha già fatto il lavoro
      return;
    }

    // ── Caso 2: drag gruppo ────────────────────────────────────────────────
    if (aP.kind === 'G') {
      const aG = gruppi.find(g => g.id === aP.id);
      if (!aG) return;

      // Vincolo MISTO: un gruppo può cambiare parent ma non livello
      // → si può droppare solo su un altro gruppo del medesimo livello (riordino)
      // OPPURE su un gruppo del livello-padre (cambia parent)

      if (oP.kind !== 'G' && oP.kind !== 'GE') return; // solo su gruppi
      const oG = gruppi.find(g => g.id === (oP.kind === 'GE' ? oP.id : oP.id));
      if (!oG) return;

      // Sotto-caso 2a: drop su gruppo dello stesso livello → riordino tra fratelli
      // (richiede stesso parent; se parent diverso, è un cambio di parent + reorder)
      if (oG.livello === aG.livello) {
        // Se hanno parent diverso: cambio parent e mi metto accanto al target
        if (oG.parentId !== aG.parentId) {
          setGruppi(curr => {
            const targetSiblings = curr
              .filter(g => g.parentId === oG.parentId && g.livello === aG.livello && g.id !== aG.id)
              .sort((a, b) => a.ordine - b.ordine);
            const oIdx = targetSiblings.findIndex(g => g.id === oG.id);
            const newSiblings = [
              ...targetSiblings.slice(0, oIdx),
              { ...aG, parentId: oG.parentId },
              ...targetSiblings.slice(oIdx),
            ];
            return curr
              .filter(g => g.id !== aG.id)
              .concat(newSiblings)
              .map(g => {
                const idx = newSiblings.findIndex(x => x.id === g.id);
                if (idx >= 0) return { ...g, ordine: idx + 1 };
                return g;
              });
          });
          return;
        }
        // Stesso parent: riordino classico
        const fratelli = gruppi
          .filter(g => g.parentId === aG.parentId && g.livello === aG.livello)
          .sort((a, b) => a.ordine - b.ordine);
        const oldIdx = fratelli.findIndex(g => g.id === aG.id);
        const newIdx = fratelli.findIndex(g => g.id === oG.id);
        if (oldIdx === -1 || newIdx === -1) return;
        const riordinato = arrayMove(fratelli, oldIdx, newIdx);
        setGruppi(curr =>
          curr.map(g => {
            const idxNuovo = riordinato.findIndex(x => x.id === g.id);
            if (idxNuovo === -1) return g;
            return { ...g, ordine: idxNuovo + 1 };
          })
        );
        return;
      }

      // Sotto-caso 2b: drop su gruppo del livello-padre → cambia parent
      // (es. una L2 droppata su una L1 diventa figlia di quella L1)
      if (oG.livello === aG.livello - 1) {
        setGruppi(curr => {
          const fratelliNuoviParent = curr
            .filter(g => g.parentId === oG.id && g.livello === aG.livello)
            .sort((a, b) => a.ordine - b.ordine);
          const nuovoOrdine =
            (fratelliNuoviParent.reduce((m, g) => Math.max(m, g.ordine), 0)) + 1;
          return curr.map(g =>
            g.id === aG.id ? { ...g, parentId: oG.id, ordine: nuovoOrdine } : g
          );
        });
        return;
      }

      // Tutti gli altri drop sono ignorati (vincolo livelli)
      return;
    }
  };

  // ── Render gruppi root con focus ──────────────────────────────────────────
  const renderCtx: RenderCtx = {
    gruppi, righe,
    openL1, toggleL1: (id) => toggle(openL1, setOpenL1, id),
    openL2, toggleL2: (id) => toggle(openL2, setOpenL2, id),
    openL3, toggleL3: (id) => toggle(openL3, setOpenL3, id),
    focusId, setFocusId, isInFocus,
    righeIn, totaleRigheRicorsivo, importoRicorsivo,
    aggiungiGruppo,
    draggingGruppoId: activeKind === 'gruppo' && activeId ? parseDndId(activeId)?.id ?? null : null,
  };

  // Determino i gruppi da renderizzare al livello root in base al focus
  const rootGruppi: Gruppo[] = useMemo(() => {
    if (!focusId) {
      return gruppi.filter(g => g.parentId === null && g.livello === 1).sort((a, b) => a.ordine - b.ordine);
    }
    // In focus: il gruppo focalizzato diventa il "root" della vista
    const f = gruppi.find(g => g.id === focusId);
    return f ? [f] : [];
  }, [gruppi, focusId]);

  const totaleGenerale = gruppi
    .filter(g => g.parentId === null)
    .reduce((s, g) => s + importoRicorsivo(g.id), 0);

  // ── Drag preview ──────────────────────────────────────────────────────────
  const dragPreview = (() => {
    if (!activeId) return null;
    const p = parseDndId(activeId);
    if (!p) return null;
    if (p.kind === 'R') {
      const r = righe.find(x => x.id === p.id);
      return r ? <RigaDragPreview r={r} /> : null;
    }
    if (p.kind === 'G') {
      const g = gruppi.find(x => x.id === p.id);
      if (!g) return null;
      return <GruppoDragPreview g={g} nRighe={totaleRigheRicorsivo(g.id)} importo={importoRicorsivo(g.id)} />;
    }
    return null;
  })();

  return (
    <Layout>
      <style>{STYLE_BLOCK}</style>
      <div className="mockup-drilldown">
        <div className="bop-page-header">
          <h1 className="bop-page-title">Mockup — Righe preventivo, drill-down 3 livelli (Proposta C)</h1>
        </div>

        <div className="mockup-note">
          <strong>Drag &amp; drop attivo</strong> · trascina dalla maniglia <span className="grip-mini"><GripIcon /></span>.
          Vincoli: gruppi cambiano <em>parent</em> ma non livello (L1↔L1, L2↔L2, L3↔L3, oppure L2 dentro un'altra L1, L3 dentro un'altra L2).
          Le righe possono andare in qualunque gruppo. Tasti freccia + Spazio per drag con tastiera.
        </div>

        {/* Breadcrumb focus */}
        {focusId && (
          <div className="breadcrumb-bar">
            <button className="breadcrumb-back" onClick={() => setFocusId(null)}>
              <ArrowLeftIcon /> Esci da focus
            </button>
            <span className="breadcrumb-sep">|</span>
            <span style={{ fontSize: 12, color: '#64748b' }}>Stai vedendo solo:</span>
            {focusPath.map((g, i) => (
              <React.Fragment key={g.id}>
                {i > 0 && <span className="breadcrumb-sep">›</span>}
                <button
                  className={`breadcrumb-segment ${i === focusPath.length - 1 ? 'current' : ''}`}
                  onClick={() => setFocusId(g.id)}
                >
                  {g.etichetta}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={rootGruppi.map(g => idGruppo(g.id))}
            strategy={verticalListSortingStrategy}
          >
            <div className="root-list">
              {rootGruppi.map(g => <SortableGruppo key={g.id} g={g} ctx={renderCtx} />)}
            </div>
          </SortableContext>

          {/* Aggiungi sezione di livello 1 (visibile solo fuori focus) */}
          {!focusId && (
            <div className="root-add">
              <AggiungiGruppoBox
                livello={1}
                parentId={null}
                onAdd={(et) => aggiungiGruppo(1, null, et)}
              />
            </div>
          )}

          <DragOverlay>
            {dragPreview}
          </DragOverlay>
        </DndContext>

        <div className="totali-card">
          <div>
            <div className="totali-label">Totale preventivo (vista cliente)</div>
            <div style={{ fontSize: 11, color: '#075985' }}>somma di tutte le righe valide</div>
          </div>
          <div className="totali-value">€ {fmt(totaleGenerale)}</div>
        </div>
      </div>
    </Layout>
  );
};

// ── Stili ───────────────────────────────────────────────────────────────────

const STYLE_BLOCK = `
.mockup-drilldown { padding: 16px 20px; }
.mockup-note {
  background: #fffbeb; border-left: 3px solid #f59e0b; padding: 10px 14px;
  font-size: 13px; color: #78350f; border-radius: 4px; margin-bottom: 16px;
}
.grip-mini { display: inline-flex; vertical-align: middle; color: #94a3b8; margin: 0 2px; }

/* Gruppi */
.gp { border: 1px solid #e2e8f0; border-radius: 8px; background: white; margin-bottom: 8px; overflow: hidden; }
.gp-l1 { border-left: 4px solid #1e3a8a; }
.gp-l2 { border-left: 4px solid #6366f1; }
.gp-l3 { border-left: 4px solid #94a3b8; }

.gp-header {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; cursor: pointer; user-select: none;
  transition: background .12s ease;
}
.gp-header:hover { background: #f8fafc; }
.gp-l1 .gp-header { background: #eef2ff; font-weight: 600; }
.gp-l2 .gp-header { background: #f5f3ff; }
.gp-l3 .gp-header { background: #f8fafc; font-style: italic; }

.gp-grip {
  color: #cbd5e1; cursor: grab; display: flex; padding: 2px;
  border-radius: 3px; touch-action: none;
}
.gp-grip:hover { color: #1e3a8a; background: rgba(30, 58, 138, 0.08); }
.gp-grip:active { cursor: grabbing; }

.gp-chevron { color: #64748b; display: flex; }
.gp-livello-tag {
  display: inline-block; padding: 1px 5px; border-radius: 3px;
  background: rgba(99,102,241,0.12); color: #4338ca;
  font-size: 9.5px; font-weight: 700; letter-spacing: .04em;
}
.gp-l1 .gp-livello-tag { background: rgba(30,58,138,0.12); color: #1e3a8a; }
.gp-l3 .gp-livello-tag { background: rgba(148,163,184,0.18); color: #475569; }

.gp-label { flex: 1; font-size: 14px; color: #0f172a; }
.gp-l1 .gp-label { font-size: 15px; }
.gp-meta { font-size: 12px; color: #64748b; font-variant-numeric: tabular-nums; }
.gp-actions { display: flex; gap: 4px; opacity: 0.4; transition: opacity .12s ease; }
.gp-header:hover .gp-actions { opacity: 1; }
.gp-actions .bop-btn-icon { padding: 4px; background: transparent; border: 0; border-radius: 4px; cursor: pointer; }
.gp-actions .bop-btn-icon:hover { background: rgba(30,58,138,0.10); color: #1e3a8a; }

.gp-body { padding: 4px 12px 12px 12px; }
.gp-children { padding-left: 20px; margin-top: 6px; }

/* Empty zone droppable per righe */
.empty-rows-zone {
  padding: 16px; margin: 6px 0;
  border: 2px dashed #cbd5e1; border-radius: 6px;
  text-align: center; font-size: 13px; color: #64748b; font-style: italic;
  transition: all .12s ease;
}
.empty-rows-zone--over {
  border-color: #6366f1; background: #eef2ff; color: #4338ca;
}

/* Bottoni "aggiungi" */
.add-gruppo-trigger {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px; margin: 6px 0;
  background: transparent; border: 1px dashed #cbd5e1;
  border-radius: 6px; cursor: pointer;
  font-size: 12.5px; color: #64748b; font-weight: 500;
  transition: all .12s ease;
}
.add-gruppo-trigger:hover {
  background: #eef2ff; border-color: #6366f1; color: #4338ca; border-style: solid;
}
.add-gruppo-trigger-ctx { color: #94a3b8; font-weight: 400; }
.add-gruppo-box {
  display: flex; gap: 8px; align-items: center;
  margin: 8px 0; padding: 8px 10px;
  background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 6px;
}
.add-gruppo-box input { flex: 1; }

.gp-add-child { padding: 4px 0; }

.link-add {
  display: inline-flex; align-items: center; gap: 4px;
  background: transparent; border: 0; padding: 4px 6px;
  font-size: 12px; color: #6366f1; font-weight: 500; cursor: pointer;
  border-radius: 3px;
}
.link-add:hover { background: #eef2ff; }
.link-add--inline { margin-top: 4px; }

.root-list { margin-bottom: 12px; }
.root-add { margin-top: 8px; }

/* Tabella righe */
.righe-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.righe-table th, .righe-table td {
  padding: 6px 8px; border-bottom: 1px solid #f1f5f9; text-align: left;
}
.righe-table th {
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .03em;
  color: #64748b; background: #f8fafc;
}
.righe-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
.righe-table tr.riga-row { transition: background .12s ease; }
.righe-table tr.riga-row:hover { background: #fafbfc; }

.col-grip {
  width: 24px; color: #cbd5e1; cursor: grab; touch-action: none;
}
.col-grip:hover { color: #1e3a8a; }
.col-num { width: 30px; color: #94a3b8; font-size: 11px; }
.col-tipo { width: 70px; }
.col-codice { width: 110px; font-family: 'JetBrains Mono', Menlo, monospace; font-size: 11.5px; color: #475569; }
.col-um { width: 50px; }
.col-azioni { width: 80px; text-align: right; }
.col-azioni .bop-btn-icon {
  background: transparent; border: 0; padding: 3px; border-radius: 3px; cursor: pointer; color: #64748b;
}
.col-azioni .bop-btn-icon:hover { background: #eef2ff; color: #1e3a8a; }

.tipo-badge {
  display: inline-block; padding: 2px 6px; border-radius: 3px;
  font-size: 10px; font-weight: 600; text-transform: uppercase;
}
.tipo-badge--ART { background: #dbeafe; color: #1e3a8a; }
.tipo-badge--CFG { background: #f0fdf4; color: #166534; }
.tipo-badge--DSC { background: #fef3c7; color: #78350f; }

/* Cluster alternative header (riga visiva fra le righe) */
.cluster-row td {
  background: #fff7ed; padding: 6px 12px; font-size: 12px; color: #9a3412;
  border-top: 1px solid #fed7aa;
}
.alt-badge {
  display: inline-block; padding: 1px 5px; border-radius: 2px;
  background: #f97316; color: white; font-size: 10px; font-weight: 700; margin-right: 6px;
}
.scelta-badge {
  display: inline-block; padding: 1px 5px; border-radius: 2px;
  background: #16a34a; color: white; font-size: 9px; font-weight: 700; margin-left: 6px;
}

/* Breadcrumb focus */
.breadcrumb-bar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; background: white;
  border: 1px solid #e2e8f0; border-radius: 8px;
  margin-bottom: 12px;
}
.breadcrumb-back {
  display: inline-flex; align-items: center; gap: 4px;
  background: transparent; border: 0; padding: 4px 8px;
  color: #1e3a8a; font-weight: 600; cursor: pointer; border-radius: 4px;
  font-size: 13px;
}
.breadcrumb-back:hover { background: #eef2ff; }
.breadcrumb-sep { color: #cbd5e1; font-size: 12px; }
.breadcrumb-segment {
  background: transparent; border: 0; padding: 4px 8px; cursor: pointer;
  color: #64748b; font-size: 13px; border-radius: 4px;
}
.breadcrumb-segment:hover { background: #f1f5f9; color: #0f172a; }
.breadcrumb-segment.current { color: #0f172a; font-weight: 600; cursor: default; }
.breadcrumb-segment.current:hover { background: transparent; }

/* Drag preview overlay */
.drag-preview {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 14px; background: white;
  border: 1px solid #6366f1; border-radius: 6px;
  box-shadow: 0 8px 20px rgba(99,102,241,0.25);
  font-size: 13px; color: #0f172a;
  cursor: grabbing;
}
.drag-preview-codice {
  font-family: 'JetBrains Mono', Menlo, monospace;
  font-size: 11px; color: #475569;
}
.drag-preview-desc { flex: 1; max-width: 380px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.drag-preview-imp { font-variant-numeric: tabular-nums; font-weight: 600; color: #16a34a; }
.drag-preview-livello {
  font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 3px;
  background: #eef2ff; color: #4338ca;
}
.drag-preview--gruppo { background: #fafbfc; }
.drag-preview--l1 { border-color: #1e3a8a; }
.drag-preview--l1 .drag-preview-livello { background: rgba(30,58,138,0.15); color: #1e3a8a; }
.drag-preview--l3 { border-color: #94a3b8; }

/* Totale */
.totali-card {
  margin-top: 16px; padding: 14px 18px; background: #f0f9ff;
  border: 1px solid #bae6fd; border-radius: 8px;
  display: flex; justify-content: space-between; align-items: center;
}
.totali-label { font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: #075985; font-weight: 600; }
.totali-value { font-size: 22px; font-weight: 700; color: #0c4a6e; font-variant-numeric: tabular-nums; }
`;

export default MockupRighePreventivoDrilldownC;
